/**
 * White-label configuration — single source of truth.
 *
 * Per-client values are injected at BUILD time via VUE_APP_* env vars
 * (see .env.example and clients/<name>/). The vanilla defaults below keep
 * the app importable, but a real Firebase config MUST be provided (via
 * .env.local or the apply-client script) for it to actually run.
 *
 * Anything that differs between white-label clients belongs here — nowhere
 * else in src/ should hardcode a brand name, colour, region or project id.
 */
const BASE_URL = process.env.BASE_URL || "/";

function list(value, fallback) {
  return (value || fallback).split(",").map((s) => s.trim()).filter(Boolean);
}

// Friendly, human-facing names for each submit endpoint (e.g. "English"/"French"
// datasets), positionally aligned to VUE_APP_SUBMIT_ENDPOINTS. Falls back to the
// raw endpoint name when no label is configured. Drives the sidebar Database selector.
function endpointLabelMap(endpoints) {
  const labels = list(process.env.VUE_APP_SUBMIT_ENDPOINT_LABELS, "");
  const map = {};
  endpoints.forEach((ep, i) => { map[ep] = labels[i] || ep; });
  return map;
}

const submitEndpoints = list(process.env.VUE_APP_SUBMIT_ENDPOINTS, "submit");

// Storage/Firestore env prefix per endpoint, positionally aligned to
// VUE_APP_SUBMIT_ENDPOINTS. Each entry must match that function's server
// ENVIRONMENT (e.g. submit->prod, submitFr->demo-fr) so images follow the
// dataset when the endpoint switcher is used. Endpoints without an entry
// fall back to the single VUE_APP_STORAGE_ENV.
function endpointStorageEnvMap(endpoints) {
  const envs = list(process.env.VUE_APP_ENDPOINT_STORAGE_ENVS, "");
  const map = {};
  endpoints.forEach((ep, i) => { if (envs[i]) map[ep] = envs[i]; });
  return map;
}

const brand = {
  name: process.env.VUE_APP_BRAND_NAME || "FieldOps",
  color: process.env.VUE_APP_BRAND_COLOR || "#0071CE",
  // Logo + favicon live in public/ so they can be swapped per client with no rebuild of code.
  logo: BASE_URL + (process.env.VUE_APP_BRAND_LOGO || "brand-logo.svg"),

  // Cloud Functions
  functionsRegion: process.env.VUE_APP_FUNCTIONS_REGION || "europe-west3",
  defaultEndpoint: process.env.VUE_APP_SUBMIT_ENDPOINT || "submit",
  endpoints: submitEndpoints,
  endpointLabels: endpointLabelMap(submitEndpoints),
  // Storage/Firestore env prefix — MUST match the server's ENVIRONMENT for this endpoint.
  storageEnv: process.env.VUE_APP_STORAGE_ENV || "prod",
  endpointStorageEnvs: endpointStorageEnvMap(submitEndpoints),

  defaultLocale: process.env.VUE_APP_I18N_LOCALE || "fr",

  // Firebase web app config (per client). Undefined in vanilla until a client config is applied.
  firebase: {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID,
    measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
  },
};

export default brand;
